import { useCallback, useState } from "react";

export type SortDirection = "asc" | "desc";

export interface SortState<T extends string = string> {
  column: T;
  direction: SortDirection;
}

interface UseSortableTableOptions<T extends string> {
  storageKey: string;
  defaultColumn: T;
  defaultDirection?: SortDirection;
}

const STORAGE_PREFIX = "sech_table_sort_";

function loadSortState<T extends string>(
  storageKey: string,
  defaultColumn: T,
  defaultDirection: SortDirection
): SortState<T> {
  try {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.column && parsed.direction) {
        return parsed as SortState<T>;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return { column: defaultColumn, direction: defaultDirection };
}

function saveSortState<T extends string>(
  storageKey: string,
  state: SortState<T>
): void {
  try {
    localStorage.setItem(
      `${STORAGE_PREFIX}${storageKey}`,
      JSON.stringify(state)
    );
  } catch {
    // Ignore storage errors
  }
}

export function useSortableTable<T extends string>({
  storageKey,
  defaultColumn,
  defaultDirection = "asc",
}: UseSortableTableOptions<T>) {
  const [sortState, setSortState] = useState<SortState<T>>(() =>
    loadSortState(storageKey, defaultColumn, defaultDirection)
  );

  const handleSort = useCallback(
    (column: T) => {
      setSortState((prev) => {
        const newState: SortState<T> =
          prev.column === column
            ? {
                column,
                direction: prev.direction === "asc" ? "desc" : "asc",
              }
            : { column, direction: "asc" };

        saveSortState(storageKey, newState);
        return newState;
      });
    },
    [storageKey]
  );

  const resetSort = useCallback(() => {
    const defaultState: SortState<T> = {
      column: defaultColumn,
      direction: defaultDirection,
    };
    setSortState(defaultState);
    saveSortState(storageKey, defaultState);
  }, [storageKey, defaultColumn, defaultDirection]);

  const sortData = useCallback(
    <D>(
      data: D[],
      accessor: (
        item: D,
        column: T
      ) => string | number | Date | boolean | null | undefined
    ): D[] => {
      return [...data].sort((a, b) => {
        const aVal = accessor(a, sortState.column);
        const bVal = accessor(b, sortState.column);

        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return sortState.direction === "asc" ? 1 : -1;
        if (bVal == null) return sortState.direction === "asc" ? -1 : 1;

        let comparison = 0;
        if (typeof aVal === "string" && typeof bVal === "string") {
          comparison = aVal.localeCompare(bVal);
        } else if (aVal instanceof Date && bVal instanceof Date) {
          comparison = aVal.getTime() - bVal.getTime();
        } else if (typeof aVal === "boolean" && typeof bVal === "boolean") {
          comparison = aVal === bVal ? 0 : aVal ? -1 : 1;
        } else {
          comparison = Number(aVal) - Number(bVal);
        }

        return sortState.direction === "asc" ? comparison : -comparison;
      });
    },
    [sortState]
  );

  return {
    sortColumn: sortState.column,
    sortDirection: sortState.direction,
    handleSort,
    resetSort,
    sortData,
  };
}
